import React, { useState } from "react";
import { Trash2, Edit2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../../lib/api";

export function AdminHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <h1 className="font-serif text-2xl md:text-3xl font-bold text-brand-text">{title}</h1>
        {subtitle && <p className="text-brand-muted mt-1 text-sm">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, color = "primary" }) {
  const colors = {
    primary: "bg-brand-primary-light text-brand-primary",
    accent: "bg-brand-accent/10 text-brand-accent",
    secondary: "bg-brand-secondary-light text-brand-secondary",
    warn: "bg-orange-100 text-orange-600",
  };
  return (
    <div className="card-elev p-5 md:p-6 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]}`}><Icon size={22} /></div>
      <div>
        <div className="text-xs uppercase tracking-widest text-brand-muted font-semibold">{label}</div>
        <div className="font-serif text-2xl md:text-3xl font-bold text-brand-text mt-1">{value}</div>
      </div>
    </div>
  );
}

export function Modal({ open, onClose, title, children, size = "md" }) {
  if (!open) return null;
  const sizes = { sm: "max-w-md", md: "max-w-2xl", lg: "max-w-4xl" };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl w-full ${sizes[size]} max-h-[90vh] overflow-y-auto scroll-thin`} onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 flex items-center justify-between p-5 border-b border-brand-border bg-white z-10">
          <h3 className="font-serif text-xl font-bold text-brand-text">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-brand-bg" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="p-5 md:p-6">{children}</div>
      </div>
    </div>
  );
}

export function ConfirmDelete({ onConfirm, children = "Delete" }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (!window.confirm("Are you sure? This action cannot be undone.")) return;
    setBusy(true);
    try { await onConfirm(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <button disabled={busy} onClick={handle} className="p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50" aria-label="Delete" data-testid="delete-btn">
      <Trash2 size={16} />
    </button>
  );
}

export function EditBtn({ onClick }) {
  return <button onClick={onClick} className="p-2 rounded-lg text-brand-primary hover:bg-brand-primary-light" aria-label="Edit" data-testid="edit-btn"><Edit2 size={16} /></button>;
}

export function AddBtn({ onClick, label = "Add New" }) {
  return <button onClick={onClick} className="btn-primary !py-2 !px-4 text-sm" data-testid="add-btn"><Plus size={16} /> {label}</button>;
}

export function Field({ label, children }) {
  return <div><label className="form-label">{label}</label>{children}</div>;
}
